import React, { useEffect, useState } from "react";
import { Box, Flex, Grid, Input, Label, Select, Switch } from "theme-ui";

import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { themevals } from "theme/themevals";
import { getLeadSettingsFull, updateLeadSettings, sendLeadDigest, type LeadSettings as LeadSettingsValues, LEAD_SETTINGS_DEFAULTS } from "api/Leads";
import MaintenanceOpportunitySettingsCard from "containers/MaintenanceOpportunities/SettingsCard";

/**
 * Company Settings > Leads tab. The company-wide knobs for the Leads
 * module (master switch, lead markup, quote links, follow-ups,
 * notifications, default owner). Self-contained like the API tab: it
 * reads and writes its own endpoint (GET/PUT /leads/settings), so it has
 * its own Save button - the page's "Save Company" does not touch it.
 * Every value is a company_data row; absent = default.
 */

const SectionCard: React.FC<{ title: string; children: React.ReactNode; description?: string }> = ({ title, children, description }) => (
  <Box
    sx={{
      backgroundColor: "#FFFFFF",
      borderRadius: "6px",
      border: "1px solid",
      borderColor: themevals.colors.inputOutline,
      boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
      marginBottom: "24px",
      padding: "24px",
    }}
  >
    <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "16px", fontWeight: 600, color: "base_800", marginBottom: "4px" }}>{title}</Box>
    {description && <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "13px", color: "base_500", marginBottom: "20px" }}>{description}</Box>}
    {children}
  </Box>
);

const FieldLabel: React.FC<{ label: string; description?: string }> = ({ label, description }) => (
  <>
    <Label sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, color: "base_800", marginBottom: "4px", display: "block" }}>{label}</Label>
    {description && <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: "base_400", marginBottom: "6px" }}>{description}</Box>}
  </>
);

const inputSx = { fontFamily: "Poppins", fontSize: "14px", border: "1px solid", borderColor: "base_300", borderRadius: "6px", padding: "8px 12px", backgroundColor: "#fff" };

const ToggleRow: React.FC<{ label: string; description: string; checked: boolean; onChange: (v: boolean) => void }> = ({ label, description, checked, onChange }) => (
  <Flex sx={{ alignItems: "center", justifyContent: "space-between", gap: "20px", paddingY: "10px" }}>
    <Box>
      <Box sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, color: "base_800" }}>{label}</Box>
      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: "base_500" }}>{description}</Box>
    </Box>
    <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />
  </Flex>
);

export default function LeadSettings() {
  const toast = useToast();
  const [form, setForm] = useState<LeadSettingsValues>({ ...LEAD_SETTINGS_DEFAULTS });
  const [saved, setSaved] = useState<LeadSettingsValues>({ ...LEAD_SETTINGS_DEFAULTS });
  const [owners, setOwners] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestEmail, setDigestEmail] = useState("");
  const [digestResult, setDigestResult] = useState<string | null>(null);

  useEffect(() => {
    getLeadSettingsFull().then((res: any) => {
      setLoading(false);
      if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
      setForm({ ...LEAD_SETTINGS_DEFAULTS, ...res.settings });
      setSaved({ ...LEAD_SETTINGS_DEFAULTS, ...res.settings });
      setOwners(res.owners ?? []);
    });
  }, []); // eslint-disable-line

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const set = <K extends keyof LeadSettingsValues>(k: K, v: LeadSettingsValues[K]) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    const res: any = await updateLeadSettings(form);
    setSaving(false);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    setForm({ ...LEAD_SETTINGS_DEFAULTS, ...res });
    setSaved({ ...LEAD_SETTINGS_DEFAULTS, ...res });
    toast.add("Lead settings saved. Reload to see the Leads menu change.", "var(--theme-ui-colors-success)");
  };

  if (loading) return <Box sx={{ fontFamily: "Poppins", fontSize: "13px", color: "base_500", padding: "24px" }}>Loading…</Box>;

  return (
    <Box>
      <Flex sx={{ justifyContent: "space-between", alignItems: "center", marginBottom: "16px", gap: "12px", flexWrap: "wrap" }}>
        <Box sx={{ fontFamily: "Poppins", fontSize: "13px", color: "base_500" }}>
          Company-wide behaviour of the Leads module. These save separately from the rest of Company Settings.
        </Box>
        <BasicButton type="button" onClick={save} disabled={!dirty || saving} $submitting={saving}>
          {dirty ? "Save Lead Settings" : "Saved"}
        </BasicButton>
      </Flex>

      <SectionCard title="Feature Toggle" description="Master switch for the Leads module. Off: the Leads menu item disappears and every lead route refuses. Nothing is deleted.">
        <ToggleRow label="Leads module enabled" description="Users still need the Lead permissions on their role." checked={form.enabled} onChange={(v) => set("enabled", v)} />
      </SectionCard>

      <SectionCard title="Lead Pricing" description="Prospects who are not yet clients pay a markup on quotes. Applied to every line at quote creation; a lead can override it on its record. The public quote page states that client pricing is lower.">
        <Grid columns={[1, 2]} gap="20px">
          <Box>
            <FieldLabel label="Lead markup (%)" description="0 turns it off. Recorded on each quote so history reads correctly after conversion." />
            <Input type="number" min={0} step={0.5} value={form.markupPercent} onChange={(e) => set("markupPercent", Number(e.target.value))} sx={inputSx} />
          </Box>
        </Grid>
      </SectionCard>

      <SectionCard title="Public Quote Links" description="How a quote reaches someone who cannot log in.">
        <Grid columns={[1, 2]} gap="20px">
          <Box>
            <FieldLabel label="Link valid for (days)" description="Extendable from the lead record without re-sending." />
            <Input type="number" min={1} value={form.quoteLinkDays} onChange={(e) => set("quoteLinkDays", Number(e.target.value))} sx={inputSx} />
          </Box>
        </Grid>
        <ToggleRow label="Require a password by default" description="The Send panel starts with the box ticked; it can be unticked per send." checked={form.quoteLinkPassword} onChange={(v) => set("quoteLinkPassword", v)} />
      </SectionCard>

      <SectionCard title="Follow-ups and Status" description="What happens to a lead automatically when something goes out to it.">
        <Grid columns={[1, 2]} gap="20px" sx={{ marginBottom: "10px" }}>
          <Box>
            <FieldLabel label="Automatic follow-up (days after a send)" description="A next action is set this many days after a proposal or quote goes out, if nothing is already due. 0 = never." />
            <Input type="number" min={0} value={form.followUpDays} onChange={(e) => set("followUpDays", Number(e.target.value))} sx={inputSx} />
          </Box>
        </Grid>
        <ToggleRow label="Move status forward on send" description="Proposal sent → Proposal sent; quote sent → Negotiating. Only from earlier statuses, never backwards." checked={form.autoStatusOnSend} onChange={(v) => set("autoStatusOnSend", v)} />
      </SectionCard>

      <SectionCard title="Notifications" description="What the team hears, and from where. Slack alerts go to the same channel as RFQs and accepted quotes; emails go to the lead's owner.">
        <Box sx={{ fontFamily: "Poppins", fontSize: "13px", color: "base_800", lineHeight: 1.8, marginBottom: "14px" }}>
          <div><b>Slack:</b> new website lead, quote accepted online, question on a quote, reply on a proposal, lead assigned, lead converted.</div>
          <div><b>Email to the owner:</b> new website lead, quote accepted, question asked, lead assigned to them, and the morning &ldquo;leads due&rdquo; digest below.</div>
          <div><b>Email to the lead:</b> website acknowledgement, the quote link, the acceptance confirmation, the portal invite on conversion.</div>
        </Box>
        <FieldLabel label="Leads due digest" description="One email per owner: overdue actions, due today, accepted quotes to convert, unopened proposals. Once per day per person; this button sends it now for testing. Leave the address blank to send to everyone with something due." />
        <Flex sx={{ gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <Input placeholder="Only to this email (optional)" value={digestEmail} onChange={(e) => setDigestEmail(e.target.value)} sx={{ ...inputSx, maxWidth: "320px" }} />
          <BasicButton scheme="secondaryOutline" type="button" keepText disabled={digestBusy} $submitting={digestBusy} onClick={async () => {
            setDigestBusy(true);
            setDigestResult(null);
            const res: any = await sendLeadDigest({ force: true, email: digestEmail.trim() || undefined });
            setDigestBusy(false);
            if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
            setDigestResult(res.reason ? `Not sent: ${res.reason}.` : `${res.sent} sent${res.skipped ? `, ${res.skipped} skipped` : ""}.`);
          }}>
            Send the digest now
          </BasicButton>
          {digestResult && <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: "base_500" }}>{digestResult}</Box>}
        </Flex>
        <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: "base_400", marginTop: "10px" }}>
          Sends itself every morning from 7am Eastern (on the app&rsquo;s scheduled job). This button sends it now for testing.
        </Box>
      </SectionCard>

      <SectionCard title="Defaults" description="Applied to new leads however they arrive - form, import, website or the proposal generator.">
        <Grid columns={[1, 2]} gap="20px">
          <Box>
            <FieldLabel label="Default owner" description="Who new leads are assigned to when nobody is chosen. Website leads always use this - set it, or new website leads email nobody." />
            <Select value={form.defaultOwnerId ?? ""} onChange={(e) => set("defaultOwnerId", e.target.value || null)} sx={inputSx}>
              <option value="">Whoever creates the lead</option>
              {owners.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </Select>
          </Box>
        </Grid>
      </SectionCard>

      <MaintenanceOpportunitySettingsCard />
    </Box>
  );
}
